import Button from "atoms/buttons/button";
import { ButtonSizes } from "atoms/constants/button-sizes";
import { ButtonStyles } from "atoms/constants/button-styles";
import { Icons } from "atoms/constants/icons";
import Icon from "atoms/icons/icon";
import PageRecord from "models/view-models/page-record";
import { useImageFilePath } from "molecules/page-block-content/image-block/use-image-file-path";
import usePageActions from "pages/notebooks/use-page-actions";
import React, { useEffect } from "react";
import DateUtils from "utilities/date-utils";
import useMostRecentlyEditedOn from "utilities/hooks/domain/notebooks/pages/use-most-recently-edited-on";
import { t } from "utilities/localization-utils";

interface SharedPublicPageHeaderProps {
    page: PageRecord | undefined;
    recipientGuid?: string;
    pageGuid?: string;
}

const BASE_CSS_CLASSNAME = "c-shared-public-page-header";

const SharedPublicPageHeader: React.FC<SharedPublicPageHeaderProps> = (
    props
) => {
    const { page, recipientGuid, pageGuid } = props;

    const { handleOnPrintClick } = usePageActions(page ?? null);
    const { imageFilePath: customLogoFilePath } = useImageFilePath(
        page?.customLogoFile,
        pageGuid,
        recipientGuid
    );
    const { mostRecentlyEditedOn, setMostRecentlyEditedOn } =
        useMostRecentlyEditedOn(page?.id);

    useEffect(() => {
        if (page?.mostRecentlyEditedOn) {
            setMostRecentlyEditedOn(new Date(page.mostRecentlyEditedOn));
        } else {
            setMostRecentlyEditedOn(undefined);
        }
    }, [page?.id, page?.mostRecentlyEditedOn, setMostRecentlyEditedOn]);

    const formattedMostRecentlyEditedOnTime = DateUtils.formatCustomDate(
        mostRecentlyEditedOn?.toISOString(),
        "MM/DD/YYYY hh:mm A"
    );

    return (
        <div className={`${BASE_CSS_CLASSNAME}`}>
            <div className={`${BASE_CSS_CLASSNAME}__main`}>
                <div className={`${BASE_CSS_CLASSNAME}__page-info`}>
                    <div className={`${BASE_CSS_CLASSNAME}__breadcrumb`}>
                        {t("sharedPage").toUpperCase()}
                    </div>
                    <h1 className={`${BASE_CSS_CLASSNAME}__title`}>
                        {page?.name}
                    </h1>
                </div>
                <div className={`${BASE_CSS_CLASSNAME}__customLogoWrapper`}>
                    <img
                        src={customLogoFilePath}
                        alt={page?.customLogoAltText}
                        className={`${BASE_CSS_CLASSNAME}__customLogo`}
                    />
                </div>
                <div className={`${BASE_CSS_CLASSNAME}__metaWrapper`}>
                    {mostRecentlyEditedOn && (
                        <div>
                            {t("lastUpdated")}{" "}
                            <span
                                className={`${BASE_CSS_CLASSNAME}__metaWrapper__date`}>
                                {`${formattedMostRecentlyEditedOnTime}.`}{" "}
                            </span>
                        </div>
                    )}
                    <span>
                        {t("sharedByName", {
                            name: page?.ownerFullName,
                        })}
                    </span>
                </div>
                <div className={`${BASE_CSS_CLASSNAME}__actions`}>
                    <Button
                        style={ButtonStyles.TertiaryAlt}
                        size={ButtonSizes.Small}
                        onClick={handleOnPrintClick}>
                        <Icon type={Icons.Print} />
                        {t("print")}
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default SharedPublicPageHeader;
